class BundleProduct extends HTMLElement {
  updateMedia(component, variant) {
    const images = this.getDisplayImages(component, variant);
    const frames = this.getMediaFrames();

    if (!images.length || !frames.length) return;

    frames.forEach((frame) => {
      const position = Number(frame.dataset.mediaPosition || 0);
      const image = images[position - 1];

      if (!image || position > 6) {
        this.setFrameHidden(frame, true);
        return;
      }

      this.setFrameHidden(frame, false);
      this.updateImageFrame(frame, image);
    });

    this.updateThumbnails(images);
  }

  getDisplayImages(component, variant) {
    const images = [];
    const variantImage = variant?.image;

    if (variantImage) {
      images.push(variantImage);
    }

    (component.images || []).forEach((image) => {
      if (image?.src && !images.some((existing) => existing.src === image.src)) {
        images.push(image);
      }
    });

    return images.slice(0, 6);
  }

  getMediaFrames() {
    return Array.from(this.querySelectorAll('.product-media .product-media-item[data-media-type="image"]'));
  }

  setFrameHidden(frame, isHidden) {
    frame.hidden = isHidden;
    frame.closest('.product-gallery__slide')?.toggleAttribute('hidden', isHidden);
  }

  updateImageFrame(frame, image) {
    const imageElement = frame.querySelector('img');

    if (!imageElement || !image?.src) return;

    const preload = new Image();
    preload.onload = () => {
      imageElement.src = image.src;
      imageElement.removeAttribute('srcset');
      imageElement.alt = image.alt || '';

      if (image.width) imageElement.width = image.width;
      if (image.height) imageElement.height = image.height;
      if (image.width && image.height) {
        imageElement.classList.toggle('img--portrait', image.height > image.width);
        imageElement.classList.toggle('img--landscape', image.width >= image.height);
        frame.style.setProperty('--real-ratio', image.width / image.height);
      }
    };
    preload.src = image.src;
  }

  updateThumbnails(images) {
    const thumbnails = Array.from(this.querySelectorAll('.product-media-thumbnails .product-thumbnail img'));

    thumbnails.forEach((thumbnail, index) => {
      const image = images[index];
      const thumbnailButton = thumbnail.closest('.product-thumbnail');

      if (!image || index >= 6) {
        thumbnailButton?.toggleAttribute('hidden', true);
        return;
      }

      thumbnailButton?.toggleAttribute('hidden', false);
      thumbnail.src = image.src;
      thumbnail.removeAttribute('srcset');
      thumbnail.alt = image.alt || '';
    });
  }
}

if (!customElements.get('bundle-product')) {
  customElements.define('bundle-product', BundleProduct);
}

class BundleComponentSelector extends HTMLElement {
  connectedCallback() {
    this.bundleProduct = this.closest('bundle-product');
    this.addButton = this.querySelector('[data-bundle-add]');
    this.errorElement = this.querySelector('[data-bundle-error]');
    this.components = this.loadComponents();

    this.components.forEach((component) => {
      component.state = {
        options: this.getInitialVariant(component)?.options?.slice() || [],
        variant: this.getInitialVariant(component),
      };
    });

    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    this.refreshAll();
  }

  loadComponents() {
    return Array.from(this.querySelectorAll('[data-component]')).map((element) => {
      const dataElement = element.querySelector('[data-component-json]');
      const data = JSON.parse(dataElement.textContent);

      return {
        ...data,
        element,
        index: Number(element.dataset.componentIndex),
      };
    });
  }

  getInitialVariant(component) {
    return (
      component.variants.find((variant) => String(variant.id) === String(component.defaultVariantId)) ||
      component.variants.find((variant) => variant.available) ||
      component.variants[0]
    );
  }

  handleClick(event) {
    const toggle = event.target.closest('[data-component-toggle]');
    const nextButton = event.target.closest('[data-component-next]');
    const addButton = event.target.closest('[data-bundle-add]');

    if (toggle) {
      this.toggleComponent(this.getComponentFromElement(toggle));
      return;
    }

    if (nextButton) {
      this.openNextComponent(nextButton);
      return;
    }

    if (addButton) {
      this.addBundleToCart();
    }
  }

  handleChange(event) {
    if (event.target.matches('[data-option-value]')) {
      this.selectOption(event.target);
    }
  }

  getComponentFromElement(element) {
    const componentElement = element.closest('[data-component]');
    return this.components.find((component) => component.element === componentElement);
  }

  toggleComponent(component) {
    if (!component) return;

    if (component.element.classList.contains('is-open')) {
      this.closeComponent(component);
    } else {
      this.openComponent(component);
    }
  }

  openComponent(component) {
    if (!component) return;

    this.components.forEach((item) => {
      item.element.classList.toggle('is-open', item === component);
    });

    this.bundleProduct?.updateMedia(component, component.state.variant);
  }

  closeComponent(component) {
    component?.element.classList.remove('is-open');
  }

  openNextComponent(button) {
    const current = this.getComponentFromElement(button);
    const next = this.components.find((component) => component.index === current.index + 1);

    if (next) {
      this.openComponent(next);
    } else {
      this.closeComponent(current);
    }
  }

  selectOption(button) {
    if (button.disabled) return;

    const component = this.getComponentFromElement(button);
    const optionGroup = button.closest('[data-option-index]');
    const optionIndex = Number(optionGroup.dataset.optionIndex);

    component.state.options[optionIndex] = button.dataset.optionValue;
    component.state.variant = this.findVariant(component, component.state.options);

    this.refreshComponent(component);
    this.bundleProduct?.updateMedia(component, component.state.variant);
    this.setError('');
  }

  findVariant(component, options) {
    return component.variants.find((variant) => this.optionsMatch(variant.options, options));
  }

  optionsMatch(variantOptions, selectedOptions) {
    return selectedOptions.every((option, index) => variantOptions[index] === option);
  }

  refreshAll() {
    this.components.forEach((component) => this.refreshComponent(component));
    this.updateAddButton();
  }

  refreshComponent(component) {
    this.updateSelectedButtons(component);
    this.updateOptionAvailability(component);
    this.updateSummary(component);
    this.updateAddButton();
  }

  updateSelectedButtons(component) {
    component.element.querySelectorAll('[data-option-index]').forEach((optionGroup) => {
      const optionIndex = Number(optionGroup.dataset.optionIndex);
      const selectedValue = component.state.options[optionIndex];
      const label = optionGroup.querySelector('[data-selected-option-label]');

      if (label) {
        label.textContent = selectedValue ? selectedValue : '';
      }

      optionGroup.querySelectorAll('[data-option-value]').forEach((button) => {
        button.checked = button.dataset.optionValue === selectedValue;
      });
    });
  }

  updateOptionAvailability(component) {
    component.element.querySelectorAll('[data-option-index]').forEach((optionGroup) => {
      const optionIndex = Number(optionGroup.dataset.optionIndex);

      optionGroup.querySelectorAll('[data-option-value]').forEach((button) => {
        const candidateOptions = component.state.options.slice();
        candidateOptions[optionIndex] = button.dataset.optionValue;
        const variant = this.findVariant(component, candidateOptions);

        const unavailable = !variant || !variant.available;

        button.disabled = unavailable;
        button.classList.toggle('option-unavailable', unavailable);
      });
    });
  }

  updateSummary(component) {
    const summary = component.element.querySelector('[data-component-summary]');

    if (!summary) return;

    const selectedText = component.state.options.filter(Boolean).join(' / ');
    summary.textContent = selectedText || 'Choose options';

    if (component.state.variant && !component.state.variant.available) {
      summary.textContent += ' - Sold out';
    }
  }

  updateAddButton() {
    if (!this.addButton) return;

    const invalidSelection = this.components.some((component) => {
      return !component.state.variant || !component.state.variant.available;
    });

    this.addButton.disabled = invalidSelection;
  }

  async addBundleToCart() {
    const invalidComponent = this.components.find((component) => {
      return !component.state.variant || !component.state.variant.available;
    });

    if (invalidComponent) {
      this.setError(`Please choose an available option for ${invalidComponent.title}.`);
      this.openComponent(invalidComponent);
      return;
    }

    const bundleInstance = `${this.dataset.bundleId}-${Date.now()}`;
    const items = this.components.map((component) => ({
      id: component.state.variant.id,
      quantity: 1,
      properties: {
        _bundle_id: this.dataset.bundleId,
        _bundle_instance: bundleInstance,
        _bundle_name: this.dataset.bundleTitle,
        _bundle_component: component.title,
      },
    }));

    this.setLoading(true);
    this.setError('');

    try {
      const response = await fetch(this.getCartAddUrl(), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items,
          sections: ['cart-bubble'],
          sections_url: window.location.pathname,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || window.cartStrings?.error || 'Unable to add bundle.');
      }

      this.updateCartBubble(data.sections?.['cart-bubble']);
      document.dispatchEvent(new CustomEvent('product:added-to-cart', { detail: { items } }));
      await this.openCartDrawer();
    } catch (error) {
      this.setError(error.message || window.cartStrings?.error || 'Unable to add bundle.');
    } finally {
      this.setLoading(false);
    }
  }

  getCartAddUrl() {
    return window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
  }

  updateCartBubble(html) {
    if (!html) return;

    const current = document.getElementById('CartBubble');
    if (!current) return;

    const container = document.createElement('div');
    container.innerHTML = html;
    const incoming = container.querySelector('#CartBubble');

    if (!incoming) return;

    current.className = incoming.className;
    current.innerHTML = incoming.innerHTML;
  }

  async openCartDrawer() {
    const cartModal = document.querySelector('cart-modal');

    if (cartModal?.reloadContent) {
      await cartModal.reloadContent();
    }

    if (cartModal?.show) {
      cartModal.show();
    }
  }

  setLoading(isLoading) {
    if (!this.addButton) return;

    this.addButton.disabled = isLoading;
    this.addButton.toggleAttribute('data-loading', isLoading);
    this.addButton.setAttribute('aria-busy', isLoading ? 'true' : 'false');

    if (!isLoading) {
      this.updateAddButton();
    }
  }

  setError(message) {
    if (this.errorElement) {
      this.errorElement.textContent = message;
    }
  }
}

if (!customElements.get('bundle-component-selector')) {
  customElements.define('bundle-component-selector', BundleComponentSelector);
}
